import React from 'react';
import HotelBookingCards from './HotelBookingCards';

const HotelBookingsDisplay = ({ 
    bookings,
    loading,
    onEdit,
    onDelete,
    onCheckIn,
    onCheckOut,
    onEditVetCheckin
}) => {
    const formatDateHeader = (dateString) => {
        if (!dateString) return 'No date';
        try {
            let date;
            // YYYY-MM-DD strings (our group keys) must be parsed as local time,
            // not UTC, to avoid a one-day shift in negative-offset timezones.
            if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                const [y, m, d] = dateString.split('-').map(Number);
                date = new Date(y, m - 1, d);
            } else {
                date = dateString instanceof Date ? dateString : new Date(dateString);
            }
            if (isNaN(date.getTime())) return 'Invalid date';
            return date.toLocaleDateString('es-ES', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });
        } catch (error) {
            return 'Invalid date';
        }
    };

    // Use the UTC date string (YYYY-MM-DD) so dates stored as UTC midnight
    // are not shifted to the previous day in negative-offset timezones.
    const toDateStr = (date) => {
        const d = date instanceof Date ? date : new Date(date);
        return d.toISOString().slice(0, 10);
    };
    const localDateStr = (offset = 0) => {
        const d = new Date();
        d.setDate(d.getDate() + offset);
        return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    };

    const isToday = (date) => toDateStr(date) === localDateStr(0);
    const isTomorrow = (date) => toDateStr(date) === localDateStr(1);
    const isUpcoming = (date) => {
        const ds = toDateStr(date);
        return ds > localDateStr(0) && ds <= localDateStr(4);
    };

    // Categorize bookings
    const categorizeBookings = () => {
        const categories = {
            checkedIn: [],
            checkingInToday: [],
            checkingOutToday: [],
            upcomingCheckIns: [],
            upcomingCheckOuts: []
        };

        bookings.forEach(booking => {
            const checkInDate = new Date(booking.checkIn);
            const checkOutDate = new Date(booking.checkOut);

            // Currently checked in guests
            if (booking.status === 'checked-in') {
                categories.checkedIn.push(booking);
            }
            
            // Checking in today (booked status)
            if (booking.status === 'booked' && isToday(checkInDate)) {
                categories.checkingInToday.push(booking);
            }
            
            // Checking out today — include 'booked' too: dog may be leaving even if
            // check-in was never formally recorded in the system.
            if ((booking.status === 'checked-in' || booking.status === 'booked') && isToday(checkOutDate)) {
                categories.checkingOutToday.push(booking);
            }
            
            // Upcoming check-ins (next few days)
            if (booking.status === 'booked' && isUpcoming(checkInDate) && !isToday(checkInDate)) {
                categories.upcomingCheckIns.push(booking);
            }
            
            // Upcoming check-outs (next few days)
            if (booking.status === 'checked-in' && isUpcoming(checkOutDate) && !isToday(checkOutDate)) {
                categories.upcomingCheckOuts.push(booking);
            }
        });

        // Sort each category by date
        Object.keys(categories).forEach(key => {
            categories[key].sort((a, b) => {
                const dateA = key.includes('checkOut') || key.includes('checkedIn') ? new Date(a.checkOut) : new Date(a.checkIn);
                const dateB = key.includes('checkOut') || key.includes('checkedIn') ? new Date(b.checkOut) : new Date(b.checkIn);
                return dateA - dateB;
            });
        });

        return categories;
    };

    // Group upcoming bookings by date for better display.
    // Use the UTC ISO date string (YYYY-MM-DD) as the key so dates stored as
    // UTC midnight are not shifted to the previous day in negative-offset timezones.
    const groupBookingsByDate = (bookings, dateField = 'checkIn') => {
        const grouped = {};
        bookings.forEach(booking => {
            const dateKey = new Date(booking[dateField]).toISOString().slice(0, 10);
            if (!grouped[dateKey]) grouped[dateKey] = [];
            grouped[dateKey].push(booking);
        });
        return grouped;
    };

    if (loading) {
        return <div className="loading">Cargando reservas de hotel...</div>;
    }

    const categorizedBookings = categorizeBookings();
    const upcomingCheckInsGrouped = groupBookingsByDate(categorizedBookings.upcomingCheckIns, 'checkIn');
    const upcomingCheckOutsGrouped = groupBookingsByDate(categorizedBookings.upcomingCheckOuts, 'checkOut');

    return (
        <div className="hotel-bookings-display">
            {/* Current Guests */}
            <div className="bookings-section current-guests-section">
                <h2>🏨 Huéspedes actuales</h2>
                <p className="section-subtitle">Perros hospedados actualmente en el hotel</p>

                {categorizedBookings.checkedIn.length === 0 ? (
                    <div className="no-bookings">
                        <p>No hay huéspedes ingresados actualmente.</p>
                        <p>🏨 ¡El hotel está listo para nuevas llegadas!</p>
                    </div>
                ) : (
                    <div className="bookings-grid">
                        <HotelBookingCards
                            bookings={categorizedBookings.checkedIn}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onCheckIn={onCheckIn}
                            onCheckOut={onCheckOut}
                            onEditVetCheckin={onEditVetCheckin}
                        />
                    </div>
                )}
            </div>

            {/* Today's Activity */}
            <div className="bookings-section todays-activity-section">
                <h2>📅 Actividad de hoy</h2>
                <p className="section-subtitle">{formatDateHeader(new Date())}</p>
                
                {/* Check-ins Today */}
                {categorizedBookings.checkingInToday.length > 0 && (
                    <div className="activity-subsection">
                        <h3 className="activity-header">
                            🛎️ Llegadas hoy ({categorizedBookings.checkingInToday.length})
                        </h3>
                        <div className="bookings-grid">
                            <HotelBookingCards
                                bookings={categorizedBookings.checkingInToday}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                onCheckIn={onCheckIn}
                                onCheckOut={onCheckOut}
                            />
                        </div>
                    </div>
                )}

                {/* Check-outs Today */}
                {categorizedBookings.checkingOutToday.length > 0 && (
                    <div className="activity-subsection">
                        <h3 className="activity-header">
                            🧳 Salidas hoy ({categorizedBookings.checkingOutToday.length})
                        </h3>
                        <div className="bookings-grid">
                            <HotelBookingCards
                                bookings={categorizedBookings.checkingOutToday}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                onCheckIn={onCheckIn}
                                onCheckOut={onCheckOut}
                            />
                        </div>
                    </div>
                )}

                {categorizedBookings.checkingInToday.length === 0 && categorizedBookings.checkingOutToday.length === 0 && (
                    <div className="no-bookings">
                        <p>No hay entradas ni salidas programadas para hoy.</p>
                        <p>🌟 ¡Un día tranquilo en el hotel!</p>
                    </div>
                )}
            </div>

            {/* Upcoming Check-ins */}
            {categorizedBookings.upcomingCheckIns.length > 0 && (
                <div className="bookings-section upcoming-section">
                    <h2>📋 Próximas llegadas</h2>
                    <p className="section-subtitle">Próximos días</p>
                    
                    <div className="upcoming-bookings">
                        {Object.keys(upcomingCheckInsGrouped)
                            .sort((a, b) => new Date(a) - new Date(b))
                            .map(dateKey => {
                                const bookingsForDate = upcomingCheckInsGrouped[dateKey];

                                return (
                                    <div key={dateKey} className="date-group">
                                        <h3 className="date-header">
                                            🛎️ {formatDateHeader(dateKey)}
                                            <span className="booking-count">
                                                ({bookingsForDate.length} llegada{bookingsForDate.length !== 1 ? 's' : ''})
                                            </span>
                                        </h3>
                                        <div className="bookings-grid">
                                            <HotelBookingCards
                                                bookings={bookingsForDate}
                                                onEdit={onEdit}
                                                onDelete={onDelete}
                                                onCheckIn={onCheckIn}
                                                onCheckOut={onCheckOut}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            )}

            {/* Upcoming Check-outs */}
            {categorizedBookings.upcomingCheckOuts.length > 0 && (
                <div className="bookings-section upcoming-section">
                    <h2>🧳 Próximas salidas</h2>
                    <p className="section-subtitle">Próximos días</p>
                    
                    <div className="upcoming-bookings">
                        {Object.keys(upcomingCheckOutsGrouped)
                            .sort((a, b) => new Date(a) - new Date(b))
                            .map(dateKey => {
                                const bookingsForDate = upcomingCheckOutsGrouped[dateKey];

                                return (
                                    <div key={dateKey} className="date-group">
                                        <h3 className="date-header">
                                            🧳 {formatDateHeader(dateKey)}
                                            <span className="booking-count">
                                                ({bookingsForDate.length} salida{bookingsForDate.length !== 1 ? 's' : ''})
                                            </span>
                                        </h3>
                                        <div className="bookings-grid">
                                            <HotelBookingCards
                                                bookings={bookingsForDate}
                                                onEdit={onEdit}
                                                onDelete={onDelete}
                                                onCheckIn={onCheckIn}
                                                onCheckOut={onCheckOut}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            )}

            {/* No upcoming activity */}
            {categorizedBookings.upcomingCheckIns.length === 0 && categorizedBookings.upcomingCheckOuts.length === 0 && (
                <div className="bookings-section">
                    <h2>📋 Actividad próxima</h2>
                    <div className="no-bookings">
                        <p>No hay entradas ni salidas próximas en los siguientes días.</p>
                        <p>📝 ¡Buen momento para planear y crear nuevas reservas!</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HotelBookingsDisplay;